<template>
  <master-layout pageTitle="Integrations">
    <div class="tabs-container">
      <VTabs :activeid="activeTab" connotation="cta" tabs-layout="stretch" @change="(e) => activeTab = e.target?.id || e.detail?.id">
        <VTab id="connected" :label="`Connected${connectedConnectors.length > 0 ? ' (' + connectedConnectors.length + ')' : ''}`"></VTab>
        <VTab id="not-connected" :label="`Available${notConnectedConnectors.length > 0 ? ' (' + notConnectedConnectors.length + ')' : ''}`"></VTab>
        <VTabPanel>
          <!-- Connected Integrations Tab -->
          <div class="tab-content">
            <div class="accordion-header" @click="isConnectorsOpen = !isConnectorsOpen">
              <span class="accordion-title">Connectors</span>
              <VIcon :name="isConnectorsOpen ? 'chevron-up-line' : 'chevron-down-line'" :size="-5" />
            </div>
            <transition name="accordion-slide">
            <div v-show="isConnectorsOpen">
            <div v-if="isLoadingMoengage || isLoadingSfmc" class="optionsContainer cust_brdr loading-state">
              <div class="connector-item">
                <span class="skeleton-text skeleton-circle" style="width: 48px; height: 48px;"></span>
                <div class="skeleton-content">
                  <span class="skeleton-text" style="width: 60%; height: 16px;"></span>
                  <span class="skeleton-text" style="width: 80%; height: 14px;"></span>
                </div>
                <VProgressRing class="vvd-spinner vvd-spinner-sm" connotation="cta" indeterminate />
              </div>
            </div>

            <div v-else-if="connectedConnectors.length === 0" class="optionsContainer cust_brdr empty-state">
              <VIcon name="open-line" :size="-2" />
              <h3>No Connected Integrations</h3>
              <p>You haven't connected any integrations yet. Check the Available tab to get started.</p>
            </div>

            <div v-else class="connectors-grid">
              <div
                v-for="connector in connectedConnectors"
                :key="connector.id"
                class="connector-card"
              >
                <div class="connector-card-header">
                  <div class="connector-avatar" @click="navigateToConnector(connector)">
                    <img :src="connector.logo" :alt="connector.name + ' logo'" />
                  </div>
                  <div class="connector-info cursorPointer" @click="navigateToConnector(connector)">
                    <h2 class="connector-name">{{ connector.name }}</h2>
                    <p class="connector-description">{{ connector.description }}</p>
                  </div>
                  <VButton
                    class="vvd-btn-danger-outline"
                    connotation="announcement"
                    appearance="outlined"
                    :label="disconnectingId === connector.id ? 'Disconnecting...' : 'Disconnect'"
                    :disabled="disconnectingId === connector.id"
                    @click.stop="openDisconnectConfirm(connector)"
                  ></VButton>
                </div>
                <div class="connector-card-footer">
                  <span class="linked-account-label">Linked Account</span>
                  <span class="linked-account-value">{{ connector.linkedAccount || '-' }}</span>
                </div>
              </div>
            </div>
            </div>
            </transition>
          </div>
        </VTabPanel>
        <VTabPanel>
          <!-- Available Integrations Tab -->
          <div class="tab-content">
            <div v-if="isLoadingMoengage || isLoadingSfmc" class="optionsContainer cust_brdr loading-state">
              <div class="connector-item">
                <span class="skeleton-text skeleton-circle" style="width: 48px; height: 48px;"></span>
                <div class="skeleton-content">
                  <span class="skeleton-text" style="width: 60%; height: 16px;"></span>
                  <span class="skeleton-text" style="width: 80%; height: 14px;"></span>
                </div>
                <VProgressRing class="vvd-spinner vvd-spinner-sm" connotation="cta" indeterminate />
              </div>
            </div>

            <div v-else-if="notConnectedConnectors.length === 0" class="optionsContainer cust_brdr empty-state">
              <VIcon name="check-circle-line" :size="-2" />
              <h3>All Integrations Connected</h3>
              <p>You have connected all available integrations!</p>
            </div>

            <div v-else class="optionsContainer cust_brdr">
              <div
                v-for="connector in notConnectedConnectors"
                :key="connector.id"
                class="connector-item cursorPointer"
                @click="navigateToConnector(connector)"
              >
                <div class="connector-avatar">
                  <img :src="connector.logo" :alt="connector.name + ' logo'" />
                </div>
                <div class="connector-info">
                  <h2 class="connector-name">{{ connector.name }}</h2>
                  <span class="status-tag status-available">
                    <VIcon name="check-circle-line" :size="-5" />
                    Available
                  </span>
                </div>
                <VButton
                  class="vvd-btn-secondary"
                  connotation="cta"
                  appearance="outlined"
                  :label="connectingId === connector.id ? 'Connecting...' : 'Connect'"
                  :disabled="connectingId === connector.id"
                ></VButton>
              </div>
            </div>
          </div>
        </VTabPanel>
      </VTabs>
    </div>

    <!-- Disconnect Confirmation -->
    <VDialog :open="!!connectorToDisconnect" headline="Are you sure?" modal @close="connectorToDisconnect = null">
      <template #body>
        <p class="vtext-body">You want to disconnect your {{ connectorToDisconnect?.name }} account?</p>
      </template>
      <template #action-items>
        <VButton class="vvd-btn-cancel" appearance="filled" label="Cancel" @click="connectorToDisconnect = null"></VButton>
        <VButton class="vvd-btn-primary" connotation="cta" appearance="filled" label="Yes" @click="disconnectIntegration"></VButton>
      </template>
    </VDialog>
  </master-layout>
</template>

<script>
import { environment } from '@/shared/env.js';
import apiInterceptor from '@/shared/apiInterceptor';
import { useAuthStore } from '@/stores/auth';
import { useUIStore } from '@/stores/ui';
import { hasPermission } from '@/shared/access-control';
import { useSocialChannels } from '@/composables/useSocialChannels';

export default {
  name: 'IntegrationsList',
  setup() {
    const { whatsappConnected, whatsAppSocialData } = useSocialChannels();
    return { whatsappConnected, whatsAppSocialData };
  },
  data() {
    return {
      activeTab: 'connected',
      isConnectorsOpen: true,
      moengageConfig: null,
      sfmcConfig: null,
      isLoadingMoengage: false,
      isLoadingSfmc: false,
      connectingId: null,
      disconnectingId: null,
      connectorToDisconnect: null,
      connectors: [
        {
          id: 'moengage',
          name: 'MoEngage',
          description: '',
          logo: require('@/resources/images/moengage-logo.png'),
          isConnected: false,
          linkedAccount: '',
          route: '/integrations/moengage',
          permissionKey: 'addons.moengage'
        },
        {
          id: 'sfmc',
          name: 'Salesforce',
          description: '',
          logo: require('@/resources/images/salesforce_logo.svg'),
          isConnected: false,
          linkedAccount: '',
          route: '/integrations/sfmc',
          permissionKey: 'addons.sfmc'
        }
      ]
    };
  },
  computed: {
    authStore() { return useAuthStore(); },
    user() { return this.authStore.cleanUserData; },
    permissions() { return this.authStore.getPermissions || {}; },
    connectedConnectors() {
      return this.availableConnectors.filter(connector => connector.isConnected);
    },
    notConnectedConnectors() {
      return this.availableConnectors.filter(connector => !connector.isConnected);
    },
    availableConnectors() {
      return this.connectors.filter(connector => {
        if (!connector.permissionKey) return true;
        return hasPermission(this.permissions, connector.permissionKey);
      });
    },
    moengageConnected() {
      if (!this.moengageConfig) return false;
      return this.moengageConfig.app_id &&
             this.moengageConfig.app_name &&
             this.moengageConfig.is_valid_token === true;
    },
    sfmcConnected() {
      if (!this.sfmcConfig) return false;
      return this.sfmcConfig.app_id &&
             this.sfmcConfig.app_name &&
             this.sfmcConfig.is_valid_token === true;
    }
  },
  watch: {
    moengageConnected: {
      handler(isConnected) {
        const moengageConnector = this.connectors.find(c => c.id === 'moengage');
        if (moengageConnector) {
          moengageConnector.isConnected = isConnected;
          moengageConnector.linkedAccount = isConnected
            ? (this.moengageConfig?.app_name || this.moengageConfig?.app_id || '')
            : '';
        }
      },
      immediate: true
    },
    sfmcConnected: {
      handler(isConnected) {
        const sfmcConnector = this.connectors.find(c => c.id === 'sfmc');
        if (sfmcConnector) {
          sfmcConnector.isConnected = isConnected;
          sfmcConnector.linkedAccount = isConnected
            ? (this.sfmcConfig?.app_name || this.sfmcConfig?.app_id || '')
            : '';
        }
      },
      immediate: true
    },
    user: {
      handler(newUser) {
        if (newUser?.referallshopName) {
          this.fetchMoengageStatus();
          this.fetchSfmcStatus();
        }
      },
      immediate: true
    }
  },
  methods: {
    async fetchMoengageStatus() {
      this.isLoadingMoengage = true;
      try {
        if (!this.user?.referallshopName) {
          console.warn('Shop name not available, skipping MoEngage status fetch');
          return;
        }
        const apiBaseUrl = `${environment('VUE_APP_API_URL')}settings`;
        const response = await apiInterceptor.get(`${apiBaseUrl}/v1/moengage/config-shop/${this.user.referallshopName}`);
        if (response) {
          this.moengageConfig = response;
        }
      } catch (error) {
        console.error('Error fetching MoEngage status:', error);
        if (error.response?.data?.status === 'failure') {
          const errorData = error.response.data.error;
          if (errorData?.code === '4004') {
            this.moengageConfig = null;
            return;
          }
        }
        this.moengageConfig = null;
      } finally {
        this.isLoadingMoengage = false;
      }
    },
    navigateToConnector(connector) {
      if (connector.id === 'moengage') {
        if (connector.isConnected) {
          this.$router.push('/integrations/moengage');
        } else {
          this.connectIntegration(connector);
        }
      } else if (connector.id === 'sfmc') {
        if (connector.isConnected) {
          this.$router.push('/integrations/sfmc');
        } else {
          this.connectIntegration(connector);
        }
      } else {
        this.$router.push(connector.route);
      }
    },
    openDisconnectConfirm(connector) {
      this.connectorToDisconnect = connector;
    },
    async disconnectIntegration() {
      const connector = this.connectorToDisconnect;
      if (!connector) return;
      this.connectorToDisconnect = null;
      try {
        if (!this.user?.referallshopName) {
          useUIStore().updateToast({
            open: true,
            message: 'Shop name is not available. Please check your account settings.',
            type: 'danger'
          });
          return;
        }
        this.disconnectingId = connector.id;
        useUIStore().updateLoading({ isLoading: true, message: `Disconnecting ${connector.name}...` });
        const apiBaseUrl = `${environment('VUE_APP_API_URL')}settings`;
        await apiInterceptor.post(`${apiBaseUrl}/v1/${connector.id}/disconnect/${this.user.referallshopName}`);
        useUIStore().updateToast({
          open: true,
          message: `${connector.name} disconnected successfully!`,
          type: 'success'
        });
        // Refresh the connector's status so it moves to the Available tab.
        if (connector.id === 'moengage') {
          this.moengageConfig = null;
          await this.fetchMoengageStatus();
        } else if (connector.id === 'sfmc') {
          this.sfmcConfig = null;
          await this.fetchSfmcStatus();
        }
      } catch (error) {
        console.error(`Disconnect ${connector.name} Error:`, error);
        const errorMessage = error.response?.data?.error?.message || error.message || `Failed to disconnect ${connector.name}.`;
        useUIStore().updateToast({ open: true, message: errorMessage, type: 'danger' });
      } finally {
        useUIStore().updateLoading({ isLoading: false });
        this.disconnectingId = null;
      }
    },
    async connectIntegration(connector) {
      try {
        if (!this.user?.referallshopName) {
          useUIStore().updateToast({
            open: true,
            message: 'Shop name is not available. Please check your account settings.',
            type: 'danger'
          });
          return;
        }
        if (!hasPermission(this.permissions, 'developerPortal')) {
          useUIStore().updateToast({
            open: true,
            message: 'Developer Portal access is required to connect this integration. Please contact your administrator to grant the necessary permissions.',
            type: 'danger',
            duration: 5000
          });
          return;
        }
        let waBusinessNumber = null;
        if (this.whatsappConnected && this.whatsAppSocialData) {
          const countryCode = this.whatsAppSocialData.country_code || '';
          const mobileNumber = this.whatsAppSocialData.mobile_number || '';
          waBusinessNumber = `${countryCode}${mobileNumber}`;
          if (waBusinessNumber && !waBusinessNumber.startsWith('+')) {
            waBusinessNumber = '+' + waBusinessNumber;
          }
        }
        if (!waBusinessNumber) {
          useUIStore().updateToast({
            open: true,
            message: 'WhatsApp business number is required. Please connect WhatsApp first.',
            type: 'danger'
          });
          return;
        }
        this.connectingId = connector.id;
        const apiBaseUrl = `${environment('VUE_APP_API_URL')}settings`;
        const payload = {
          wa_business_number: waBusinessNumber,
          expires_in: 2592000
        };
        const connectorType = connector.id;
        const response = await apiInterceptor.post(
          `${apiBaseUrl}/v1/${connectorType}/jumper-access-token/${this.user.referallshopName}`,
          payload
        );
        if (response?.shop_name && response?.token) {
          useUIStore().updateToast({
            open: true,
            message: `${connector.name} connected successfully!`,
            type: 'success'
          });
          this.$router.push(`/integrations/${connectorType}`);
        } else {
          useUIStore().updateToast({
            open: true,
            message: 'Unexpected response format from server.',
            type: 'warning'
          });
        }
      } catch (error) {
        console.error(`${connector.name} connection error:`, error);
        let errorMessage = 'Something went wrong. Please try again.';
        if (error.response?.status === 403) {
          errorMessage = 'Access forbidden. Please ensure you have Developer Portal access.';
        } else if (error.response?.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (error.response?.data?.status === 'failure') {
          const errorData = error.response.data.error;
          switch (errorData?.code) {
            case '4009':
              errorMessage = `${connector.name} configuration already exists.`;
              setTimeout(() => {
                this.$router.push(`/integrations/${connector.id}`);
              }, 2000);
              break;
            case '5000':
              errorMessage = 'Internal server error. Please try again later.';
              break;
            default:
              errorMessage = errorData?.message || errorMessage;
          }
        } else {
          errorMessage = error.message || errorMessage;
        }

        useUIStore().updateToast({
          open: true,
          message: errorMessage,
          type: 'danger',
          duration: 5000
        });
      } finally {
        this.connectingId = null;
      }
    },
    async fetchSfmcStatus() {
      this.isLoadingSfmc = true;
      try {
        if (!this.user?.referallshopName) {
          console.warn('Shop name not available, skipping SFMC status fetch');
          return;
        }
        const apiBaseUrl = `${environment('VUE_APP_API_URL')}settings`;
        const response = await apiInterceptor.get(`${apiBaseUrl}/v1/sfmc/config-shop/${this.user.referallshopName}`);
        if (response) {
          this.sfmcConfig = response;
        }
      } catch (error) {
        console.error('Error fetching SFMC status:', error);
        if (error.response?.data?.status === 'failure') {
          const errorData = error.response.data.error;
          if (errorData?.code === '4004') {
            this.sfmcConfig = null;
            return;
          }
        }
        this.sfmcConfig = null;
      } finally {
        this.isLoadingSfmc = false;
      }
    }
  }
};
</script>

<style scoped>
.tabs-container {
  margin-bottom: 20px;
}
.tab-content {
  background: var(--white);
  border-radius: 5px;
}

/* Connectors accordion */
.accordion-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  cursor: pointer;
  user-select: none;
}
.accordion-header:hover {
  opacity: 0.8;
}
.accordion-title {
  font-size: 16px;
  font-weight: 500;
  color: var(--black);
}
.accordion-slide-enter-active,
.accordion-slide-leave-active {
  transition: all 0.25s ease;
  max-height: 2000px;
  overflow: hidden;
}
.accordion-slide-enter-from,
.accordion-slide-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}

/* Connected connector cards (grid) */
.connectors-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 16px;
  padding: 16px;
}
.connector-card {
  border: 1px solid var(--background-primary);
  border-radius: 8px;
  padding: 16px;
  background: var(--white);
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.connector-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
}
.connector-card-header .connector-avatar {
  width: 36px;
  height: 36px;
}
.connector-card-header .connector-info {
  flex: 1;
  min-width: 0;
}
.connector-card-header .connector-name {
  font-size: 15px;
  font-weight: 500;
  color: var(--black);
  margin: 0 0 2px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.connector-description {
  font-size: 12px;
  color: var(--gray-04);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.connector-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-top: 1px solid var(--background-primary);
  padding-top: 12px;
}
.linked-account-label {
  font-size: 13px;
  color: var(--gray-04);
}
.linked-account-value {
  font-size: 13px;
  color: var(--black);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Connector list items */
.connector-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border: 1px solid var(--white);
  border-radius: 5px;
  background: var(--white);
  transition: border-color 0.2s ease;
  margin-bottom: 10px;
}
.connector-item:last-child {
  margin-bottom: 0;
}
.connector-item:hover {
  border-color: var(--purple);
}
.connector-avatar {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
}
.connector-avatar img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 8px;
}
.connector-info {
  flex: 1;
  min-width: 0;
}
.connector-name {
  font-size: 16px;
  font-weight: 500;
  color: var(--black);
  margin: 0 0 6px 0;
}
.connector-action-icon {
  color: var(--black);
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

/* Status tags */
.status-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}
.status-connected {
  background: #eafdf2;
  color: #2dd36f;
}
.status-available {
  background: #ededed;
  color: #54575e;
}

/* Empty state */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--gray-04);
}
.empty-state h3 {
  margin: 16px 0 8px 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--black);
}
.empty-state p {
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
}

/* Loading state */
.loading-state {
  padding: 16px;
}
.skeleton-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Skeleton loading */
.skeleton-text {
  display: inline-block;
  background: linear-gradient(90deg, var(--background-primary) 25%, #e8e8e8 50%, var(--background-primary) 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}
.skeleton-circle {
  border-radius: 50%;
}
@keyframes skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
